export const S3_PUT_CORS_MESSAGE =
	"Upload failed because this site is not yet allowed to send files to storage. Please try again later.";
export const S3_PUT_EXPIRED_MESSAGE = "Upload expired. Please try again.";
export const S3_PUT_REJECTED_MESSAGE = "Upload was rejected. Please try again.";
export const S3_PUT_GENERIC_MESSAGE =
	"Could not upload the file. Please try again.";

export class S3PutError extends Error {
	readonly status: number;

	constructor(message: string, status: number) {
		super(message);
		this.name = "S3PutError";
		this.status = status;
	}
}

export function mapS3PutFailure(status: number, responseText = ""): string {
	if (status === 0) return S3_PUT_CORS_MESSAGE;
	const body = responseText.toLowerCase();
	if (
		status === 403 &&
		(body.includes("expired") || body.includes("request has expired"))
	) {
		return S3_PUT_EXPIRED_MESSAGE;
	}
	if (status === 403) return S3_PUT_REJECTED_MESSAGE;
	return S3_PUT_GENERIC_MESSAGE;
}

export interface PutFileToS3Args {
	uploadUrl: string;
	file: File;
	contentType: string;
	onProgress?: (percent: number) => void;
}

/**
 * PUT the raw File to a presigned S3 URL.
 * Must not use the API client (no Authorization) and must not wrap FormData.
 */
export function putFileToS3({
	uploadUrl,
	file,
	contentType,
	onProgress,
}: PutFileToS3Args): Promise<void> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("PUT", uploadUrl);
		xhr.setRequestHeader("Content-Type", contentType);
		xhr.upload.onprogress = (event) => {
			if (!event.lengthComputable || event.total <= 0) return;
			onProgress?.(
				Math.min(100, Math.round((event.loaded / event.total) * 100)),
			);
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				onProgress?.(100);
				resolve();
				return;
			}
			reject(
				new S3PutError(
					mapS3PutFailure(xhr.status, xhr.responseText),
					xhr.status,
				),
			);
		};
		xhr.onerror = () => {
			reject(new S3PutError(S3_PUT_CORS_MESSAGE, 0));
		};
		xhr.onabort = () => {
			reject(new S3PutError(S3_PUT_GENERIC_MESSAGE, 0));
		};
		xhr.send(file);
	});
}
